import HotelStatusIcon from 'components/HotelStatusIcon'
import { SecondarySmallButton } from 'components/ReusableComponents/Button/SmallButtons'
import { CheckIcon } from 'components/ReusableComponents/Icons/Check'
import { CrossIcon } from 'components/ReusableComponents/Icons/Cross'
import TryLoginDialog from 'components/TryLoginDialog'
import { PMSsWithSyncLoginSupport, userEmailsRestartPMS } from 'constants/index'
import { PMSnames } from 'main/hotelBrandsAndChains'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import NoteCell from '../../Common/NoteCell'
import useNote from '../../Common/useNote'
import { useNoteBatchLoader } from '../../Common/NoteBatchLoaderContext'
import { Link } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import ReactTooltip from 'react-tooltip'
import internalOnlyApi from 'services/api/internalOnly'
import pmsApi from 'services/api/pms'

import { Cell, Row, Spacer } from '../../../../components/ReusableComponents/Table'
import Toggle from '../../../../components/ReusableComponents/Toggle/index'
import HotelIdCell from '../../Common/HotelIdCell'
import HotelNameCell from '../../Common/HotelNameCell'
import { ReactComponent as BugIcon } from './bug.svg'
import ElectronTelemetryModal from '../ElectronTelemetryModal'
import { getPepHealthCheckDisplay } from '../parsePepHealthCheckLastResult'
import styles from './PMSBErow.module.scss'

const PMS_HILTON_PEP = 'pep'

const NumberCell = ({ children, ...props }) => (
  <Cell className={styles.numberCell} {...props}>
    {children}
  </Cell>
)

const SyncRow = ({
  subsidiaryId,
  displayId,
  brand,
  shortName,
  groupName,
  pms,
  pmsHealthyKind,
  isAllReportHealthy,
  pmsSyncTotalReportsReceivedInLast48Hours,
  index,
  handleSetDebugData,
  problems,
  isHavingProblems,
  pmsSyncIsConnected,
  subsidiaryStatus,
  nextStartDate,
  lastResult,
  pmsSyncLastInfoReceivedOn,
  isDisabled,
  isInProgress,
  csmUserFullName,
  rowStatus,
  statusExplanation,
  hideStopTestLogin = false
}) => {
  const login = useSelector(state => state.login)
  const { registerCallback, notesCache } = useNoteBatchLoader()

  const {
    note,
    setNote,
    saveImmediately,
    isLoading: isLoadingNote,
    isSaving,
    error: noteError,
    loadNoteOnDemand,
    setNoteFromBatch
  } = useNote(subsidiaryId, 'healthCheck-pms', { batchLoad: true })

  // Register callback to receive batch-loaded notes
  useEffect(() => {
    const key = `${subsidiaryId}-healthCheck-pms`
    
    // Check if note is already in cache
    if (notesCache.has(key)) {
      setNoteFromBatch(notesCache.get(key))
    }

    // Register callback for future updates
    const unregister = registerCallback(subsidiaryId, 'healthCheck-pms', (data) => {
      setNoteFromBatch(data)
    })

    return unregister
  }, [subsidiaryId, registerCallback, notesCache, setNoteFromBatch])

  const canReEnablePMS =
    login.isInternalAdmin &&
    userEmailsRestartPMS.includes(login.currentUser.email) &&
    PMSsWithSyncLoginSupport.includes(pms)

  const isPMSSupportesLogin = PMSsWithSyncLoginSupport.includes(pms)

  const [reEnablingPmsSync, setReEnablingPmsSync] = useState(false)

  const [isTryLoginDialogVisible, setIsTryLoginDialogVisible] = useState(false)
  const [isTryingLogin, setIsTryingLogin] = useState(false)
  const [tryLoginResponse, setTryLoginResponse] = useState(null)
  const [isTryLoginSuccessful, setIsTryLoginSuccessful] = useState()
  const [isPMSSyncEnabled, setIsPMSSyncEnabled] = useState(!isDisabled)

  const [isElectronTelemetryOpen, setIsElectronTelemetryOpen] = useState(false)

  const showElectronTelemetryButton =
    login.isInternalAdmin && pms === PMS_HILTON_PEP

  const pepHealthDisplay = getPepHealthCheckDisplay(
    lastResult,
    pms,
    pmsSyncTotalReportsReceivedInLast48Hours
  )

  const pepBadgeClassName = pepHealthDisplay
    ? {
        AppNotInstalled: styles.pepBadgeAppNotInstalled,
        PepAuthorizationError: styles.pepBadgePepAuthorizationError,
        Working: styles.pepBadgeWorking,
        NeedsAttention: styles.pepBadgeNeedsAttention
      }[pepHealthDisplay.badgeKey] || styles.pepBadgeDefault
    : null

  const pepBadgesNeedRedSyncIcon = [
    'AppNotInstalled',
    'NeedsAttention',
    'PepAuthorizationError'
  ]

  const pepShowRedSyncProblemIcon =
    !!pepHealthDisplay &&
    pepBadgesNeedRedSyncIcon.includes(pepHealthDisplay.badgeKey)

  const hasProblemDebugPayload =
    problems != null &&
    problems !== '' &&
    !(Array.isArray(problems) && problems.length === 0) &&
    !(
      typeof problems === 'object' &&
      problems !== null &&
      !Array.isArray(problems) &&
      Object.keys(problems).length === 0
    )

  const showRedSyncAlertIcon =
    pepShowRedSyncProblemIcon ||
    isHavingProblems === true ||
    pmsSyncIsConnected === false

  const showProblemDetailsRow = hasProblemDebugPayload || showRedSyncAlertIcon

  function tryLogin() {
    setIsTryLoginDialogVisible(true)
    setIsTryingLogin(true)
    const credentialString = null
    internalOnlyApi
      .tryLogin({ subsidiaryId, credentialString })
      .then(res => {
        const response = res.response

        setTryLoginResponse(response)
        setIsTryLoginSuccessful(response.isSuccess)
      })
      .catch(err => {
        setTryLoginResponse(err)
        setIsTryLoginSuccessful(false)
      })
      .finally(() => {
        setIsTryingLogin(false)
      })
  }

  function closeTryLoginDialog() {
    setIsTryLoginDialogVisible(false)
    setTryLoginResponse(null)
    setIsTryLoginSuccessful(null)
  }

  const { addToast } = useToasts()

  useEffect(() => {
    setIsPMSSyncEnabled(isDisabled ? false : true)
  }, [isDisabled])

  function setStopPMSSync(status) {
    if (canReEnablePMS) {
      setReEnablingPmsSync(true)

      addToast(status ? 'Starting PMS sync...' : 'Stopping PMS sync...', {
        appearance: 'info',
        autoDismiss: true
      })

      pmsApi
        .pmsToggle({ SubsidiaryId: subsidiaryId, Pms: pms, Status: status })
        .then(() => {
          addToast(status ? 'PMS sync started' : 'PMS sync stopped', {
            appearance: 'success',
            autoDismiss: true
          })
        })
        .catch(err => {
          addToast(err.toString(), { appearance: 'error' })
          setIsPMSSyncEnabled(status)
        })
        .finally(() => {
          setIsPMSSyncEnabled(status)
          setReEnablingPmsSync(false)
        })
    } else {
      addToast('You do not have permission to perform this action', {
        appearance: 'error'
      })
    }
  }

  function pmsSyncMessage(message) {
    if (message === 'PasswordWrong') {
      return 'Password is wrong'
    } else if (message === 'PasswordExpired') {
      return 'Password is expired'
    } else if (message === 'NotIntegrated') {
      return 'Never integrated'
    } else if (message === 'SyncStopped') {
      return 'Sync stopped'
    } else if (message === 'PartiallyWorking') {
      return 'Partially working'
    } else {
      return '-'
    }
  }
  return (
    <div
      className={`${styles.rowHolder} ${rowStatus ? styles[rowStatus] : ''}`}
      title={statusExplanation}
    >
      {isElectronTelemetryOpen && (
        <ElectronTelemetryModal
          isOpen={isElectronTelemetryOpen}
          onClose={() => setIsElectronTelemetryOpen(false)}
          subsidiaryId={subsidiaryId}
          hotelDisplayId={displayId}
          hotelLabel={`${brand || ''} ${shortName || ''}`.trim()}
          openedFromPmsHealthCheckDetails
          csmUserFullName={csmUserFullName}
          lastResult={lastResult}
        />
      )}
      {isTryLoginDialogVisible && (
        <TryLoginDialog
          isTryLoginSuccessful={isTryLoginSuccessful}
          isTryingLogin={isTryingLogin}
          onClose={closeTryLoginDialog}
          response={tryLoginResponse}
          brand={brand}
          shortName={shortName}
        />
      )}
      <div className={styles.index}>{index}</div>
      <div className={styles.statusIcon}>
        <HotelStatusIcon status={subsidiaryStatus} />
      </div>
      <Row className={styles.syncTableRow}>
        <HotelIdCell displayId={displayId} />
        <HotelNameCell
          brand={brand}
          name={shortName}
          groupName={groupName}
          navigationControl={
            <Link to={`/s/${subsidiaryId}/hotelDetails`}>
              <button>Go to hotel details →</button>
            </Link>
          }
        />
        <Spacer />
        <Cell className={styles.textCell}>
          <span title={csmUserFullName}>
            {csmUserFullName
              ?.split(' ')
              .map(str => str[0])
              .join('.')}
          </span>
        </Cell>
        <Cell>
          <div className={styles.pmsStatus}>
            {isDisabled ? (
              <>
                <ReactTooltip className={styles.pmsHealthyMessage} />
                <span data-tip={pmsSyncMessage(pmsHealthyKind)}>
                  <CrossIcon fill="red" />
                </span>
              </>
            ) : isAllReportHealthy ? (
              <CheckIcon />
            ) : (
              <>
                <ReactTooltip className={styles.pmsHealthyMessage} />
                <span data-tip={pmsSyncMessage(pmsHealthyKind)}>
                  <CrossIcon />
                </span>
              </>
            )}
          </div>
        </Cell>
        <Cell className={styles.textCell}>
          {isInProgress
            ? 'Syncing now'
            : pmsSyncLastInfoReceivedOn
            ? moment.utc(pmsSyncLastInfoReceivedOn).local().format('MMM DD, YYYY h:mm A')
            : nextStartDate
            ? moment(nextStartDate).add(-1, 'day').format('MMM DD, YYYY')
            : '-'}
        </Cell>
        <NumberCell>{pmsSyncTotalReportsReceivedInLast48Hours}</NumberCell>
        <Cell className={styles.textCell}>{PMSnames[pms]}</Cell>
        <Cell
          className={`${styles.doubleCell} ${
            pepHealthDisplay ? styles.lastResultCellCompact : ''
          }`}
        >
          <div className={styles.lastResultInner}>
            {pepHealthDisplay ? (
              <div className={styles.pepLastResultMain}>
                <div className={styles.pepTopLine}>
                  <span className={`${styles.pepBadge} ${pepBadgeClassName}`}>
                    {pepHealthDisplay.badgeLabel}
                  </span>
                  <span className={styles.pepDeviceCounts}>
                    {pepHealthDisplay.activeDevices} active
                    <span className={styles.pepDeviceSep}>·</span>
                    {pepHealthDisplay.issueDevices} need attention
                  </span>
                </div>
                <div className={styles.pepStateTagRow}>
                  <span
                    className={`${styles.pepStateTag} ${
                      pepHealthDisplay.onboardingState === 'Done'
                        ? styles.pepStateTagPositive
                        : pepHealthDisplay.onboardingState === 'Pending'
                        ? styles.pepStateTagWarning
                        : styles.pepStateTagNeutral
                    }`}
                    title={`Onboarding status: ${
                      pepHealthDisplay.onboardingLabel || 'Unknown'
                    }`}
                  >
                    <span className={styles.pepStateTagKey}>Onboarding</span>
                    <span className={styles.pepStateTagValue}>
                      {pepHealthDisplay.onboardingLabel || 'Unknown'}
                    </span>
                  </span>
                  <span
                    className={`${styles.pepStateTag} ${
                      pepHealthDisplay.futureState === 'Active'
                        ? styles.pepStateTagPositive
                        : pepHealthDisplay.futureState === 'Inactive'
                        ? styles.pepStateTagWarning
                        : styles.pepStateTagNeutral
                    }`}
                    title={`Future report status: ${pepHealthDisplay.futureLabel || 'Unknown'}`}
                  >
                    <span className={styles.pepStateTagKey}>Future</span>
                    <span className={styles.pepStateTagValue}>
                      {pepHealthDisplay.futureLabel || 'Unknown'}
                    </span>
                  </span>
                </div>
              </div>
            ) : (
              <p className={styles.lastResultPlain}>
                {lastResult
                  ? lastResult.slice(0, 30)
                  : isPMSSupportesLogin
                  ? (
                      <span style={{ position: 'relative', top: 3 }}>No credentials</span>
                    )
                  : (
                      ''
                    )}
              </p>
            )}
            <div className={styles.lastResultActions}>
              {showElectronTelemetryButton && (
                <SecondarySmallButton
                  className={styles.pepDetailsBtn}
                  onClick={() => setIsElectronTelemetryOpen(true)}
                >
                  Details
                </SecondarySmallButton>
              )}
              {showElectronTelemetryButton &&
                (pepHealthDisplay?.badgeKey === 'Working' ? (
                  <span
                    className={styles.pepPmsConnectedIcon}
                    title="PMS is connected"
                    role="img"
                    aria-label="PMS is connected"
                  >
                    <CheckIcon width={16} height={16} />
                  </span>
                ) : (
                  <span
                    className={styles.pepPmsConnectedIconSpacer}
                    aria-hidden="true"
                  />
                ))}
              {showProblemDetailsRow && (
                <>
                  {hasProblemDebugPayload ? (
                    <button type="button" onClick={() => handleSetDebugData(problems)}>
                      <BugIcon width={22} fill="#cbccce" />
                    </button>
                  ) : (
                    <span
                      className={styles.pepProblemIconOnly}
                      title="Problem details — no payload to open"
                      role="img"
                      aria-label="Problem details"
                    >
                      <BugIcon width={22} fill="#cbccce" />
                    </span>
                  )}
                  {showRedSyncAlertIcon && (
                    <span
                      className={styles.pepProblemIconOnly}
                      title="PMS sync problems"
                      role="img"
                      aria-label="PMS sync problems"
                    >
                      <BugIcon width={22} fill="red" />
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        </Cell>

        {hideStopTestLogin
          ? []
          : [
              <Cell key="hc-pms-stop-test" className={styles.bugButtonCell}>
                {pms === PMS_HILTON_PEP ? (
                  <span className={styles.pepStopColumnDash} title="Not applicable for Hilton PEP">
                    —
                  </span>
                ) : (
                  <>
                    {canReEnablePMS && (
                      <Toggle
                        className={styles.groupToggle}
                        label=""
                        disabled={reEnablingPmsSync}
                        checked={isPMSSyncEnabled}
                        handleChange={e => setStopPMSSync(e.target.checked)}
                      />
                    )}
                    {PMSsWithSyncLoginSupport.includes(pms) && (
                      <SecondarySmallButton
                        style={{ transform: 'scale(0.9)' }}
                        onClick={tryLogin}
                        //disabled={!lastResult}
                      >
                        Test login
                      </SecondarySmallButton>
                    )}
                  </>
                )}
              </Cell>
            ]}
        <NoteCell
          note={note}
          setNote={setNote}
          saveImmediately={saveImmediately}
          loadNoteOnDemand={loadNoteOnDemand}
          isLoading={isLoadingNote}
          isSaving={isSaving}
          error={noteError}
        />
      </Row>
    </div>
  )
}

export default SyncRow
